export type CategoryId = 'love' | 'work' | 'friends';

export interface CategoryData {
  name: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

export interface CategoryOption extends CategoryData {
  id: CategoryId;
  description: string;
  hoverBg?: string;
}

/**
 * Get category styling and localized data based on category ID
 * @param category - The category identifier
 * @param t - Translation function
 * @returns Category data with styling and localized content
 */
export const getCategoryData = (category: string, t: any): CategoryData => {
  switch (category) {
    case 'love':
      return {
        name: t('categoryLove'),
        bgColor: 'bg-rose-50 dark:bg-rose-900',
        borderColor: 'border-rose-200 dark:border-rose-700',
        textColor: 'text-rose-800 dark:text-rose-200'
      };
    case 'work':
      return {
        name: t('categoryWork'),
        bgColor: 'bg-amber-50 dark:bg-amber-900',
        borderColor: 'border-amber-200 dark:border-amber-700',
        textColor: 'text-amber-800 dark:text-amber-200'
      };
    case 'friends':
      return {
        name: t('categoryFriends'),
        bgColor: 'bg-sky-50 dark:bg-sky-900',
        borderColor: 'border-sky-200 dark:border-sky-700',
        textColor: 'text-sky-800 dark:text-sky-200'
      };
    default:
      return {
        name: 'General',
        bgColor: 'bg-gray-50 dark:bg-gray-800',
        borderColor: 'border-gray-200 dark:border-gray-600',
        textColor: 'text-gray-800 dark:text-gray-200'
      };
  }
};

/**
 * Get complete category options for display in selection interfaces
 * @param t - Translation function
 * @returns Array of category options with full styling and content
 */
export const getCategoryOptions = (t: any): CategoryOption[] => [
  {
    id: 'love',
    name: t('categoryLove'),
    description: t('categoryLoveDesc'),
    bgColor: 'bg-rose-50 dark:bg-rose-900',
    borderColor: 'border-rose-200 dark:border-rose-700',
    textColor: 'text-rose-800 dark:text-rose-200',
    hoverBg: 'hover:bg-rose-100 dark:hover:bg-rose-800'
  },
  {
    id: 'work',
    name: t('categoryWork'),
    description: t('categoryWorkDesc'),
    bgColor: 'bg-amber-50 dark:bg-amber-900',
    borderColor: 'border-amber-200 dark:border-amber-700',
    textColor: 'text-amber-800 dark:text-amber-200',
    hoverBg: 'hover:bg-amber-100 dark:hover:bg-amber-800'
  },
  {
    id: 'friends',
    name: t('categoryFriends'),
    description: t('categoryFriendsDesc'),
    bgColor: 'bg-sky-50 dark:bg-sky-900',
    borderColor: 'border-sky-200 dark:border-sky-700',
    textColor: 'text-sky-800 dark:text-sky-200',
    hoverBg: 'hover:bg-sky-100 dark:hover:bg-sky-800'
  }
];

/**
 * Get category-specific guidance key for translations
 * @param category - The category identifier
 * @returns Translation key for category guidance
 */
export const getCategoryGuidanceKey = (category: string): string => {
  switch (category) {
    case 'love':
      return 'guidanceLove';
    case 'work':
      return 'guidanceWork';
    case 'friends':
      return 'guidanceFriends';
    default:
      return 'guidanceGeneral';
  }
};